import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, Box, Typography, CircularProgress, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { EmailGenerationProgressCardProps } from '../types';

// Animated spinner - CircularProgress already has built-in animation
const AnimatedSpinner = styled(CircularProgress)({
    // CircularProgress already animates by default
});

export const EmailGenerationProgressCard: React.FC<EmailGenerationProgressCardProps> = ({
    isGeneratingEmail,
    streamingData,
    currentSection,
    onOpenEmailGenerationPopup,
    title = 'Generating Email...',
    progressOverride,
}) => {
    // State to track filled sections based on streaming data
    const [filledSections, setFilledSections] = useState<Set<number>>(new Set());
    const [processedSections, setProcessedSections] = useState<Array<{
        index: number;
        main?: string;
        sub_text?: string;
    }>>([]);
    const [sectionTexts, setSectionTexts] = useState<Map<number, string>>(new Map());

    // Reset progress state when email generation stops
    useEffect(() => {
        if (!isGeneratingEmail) {
            setFilledSections(new Set());
            setProcessedSections([]);
            setSectionTexts(new Map());
        }
    }, [isGeneratingEmail]);

    // Process streaming data to track filled sections
    useEffect(() => {
        if (streamingData?.message) {
            const { sections: dataSections, text } = streamingData.message;

            // Update sections if available
            if (dataSections && dataSections.length > 0) {
                setProcessedSections(dataSections);
            }

            // Process text data to track which sections have received content
            if (text && text.value) {
                const sectionIndex = text.key.index;
                const textValue = text.value;
                
                // Filter out single-word type field values (like "Disclaimer", "Greeting", etc)
                // Only count actual content (multiple words or sentences)
                const isActualContent = textValue.trim().split(/\s+/).length > 1;
                
                // Update section texts only if it's actual content
                if (isActualContent) {
                    setSectionTexts(prev => {
                        const newMap = new Map(prev);
                        newMap.set(sectionIndex, textValue);
                        return newMap;
                    });
                    
                    // Update filled sections - add the section index that received content
                    setFilledSections(prev => {
                        const newSet = new Set(prev);
                        newSet.add(sectionIndex);
                        return newSet;
                    });
                }
            }
        }
    }, [streamingData]);

    // Calculate progress percentage based on filled sections (or use progressOverride when provided)
    const progressPercentage = useMemo(() => {
        if (progressOverride != null && typeof progressOverride === 'number') {
            return Math.min(Math.max(progressOverride, 0), 100);
        }
        if (!isGeneratingEmail) return 0;
        
        const sectionsToUse = processedSections.length > 0 ? processedSections : (streamingData?.message?.sections || []);
        const totalSections = Math.max(sectionsToUse.length, 1);
        const completedSections = filledSections.size;
        const calculatedProgress = Math.round((completedSections / totalSections) * 100);
        
        // Ensure progress never exceeds 100% and never goes below 0%
        return Math.min(Math.max(calculatedProgress, 0), 100);
    }, [isGeneratingEmail, filledSections, processedSections, streamingData, progressOverride]);

    if (!isGeneratingEmail) {
        return null;
    }

    return (
        <Card 
            onClick={onOpenEmailGenerationPopup}
            sx={{
                marginTop: 'auto',
                marginBottom: '16px',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                // Use a solid background so the container is not transparent
                border: (theme) => `1px solid ${theme.palette.primary.main}`,
                backgroundColor: (theme) => theme.palette.background.paper,
                boxShadow: (theme) => theme.shadows[3],
                animation: 'fadeIn 0.3s ease-in',
                '@keyframes fadeIn': {
                    from: { opacity: 0, transform: 'translateY(10px)' },
                    to: { opacity: 1, transform: 'translateY(0)' },
                },
                '&:hover': {
                    boxShadow: (theme) => theme.shadows[6],
                    transform: 'translateY(-2px)',
                    borderColor: (theme) => `${theme.palette.primary.main}60`,
                },
            }}
        >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <AnimatedSpinner 
                        size={24} 
                        thickness={4}
                        sx={{ color: 'primary.main' }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                fontWeight: 600, 
                                color: 'text.primary',
                                mb: 0.5
                            }}
                        >
                            {title}
                        </Typography>
                        {currentSection && (
                            <Typography 
                                variant="caption" 
                                sx={{ 
                                    color: 'text.secondary',
                                    display: 'block',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {currentSection.main || `Section ${currentSection.index}`}
                            </Typography>
                        )}
                        {progressPercentage > 0 && (
                            <Box sx={{ mt: 1 }}>
                                <Box sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center',
                                    mb: 0.5
                                }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Progress
                                    </Typography>
                                    <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>
                                        {progressPercentage}%
                                    </Typography>
                                </Box>
                                <Box sx={{ 
                                    width: '100%', 
                                    height: 4, 
                                    backgroundColor: 'grey.200', 
                                    borderRadius: 2,
                                    overflow: 'hidden'
                                }}>
                                    <Box sx={{ 
                                        width: `${progressPercentage}%`, 
                                        height: '100%', 
                                        backgroundColor: 'primary.main',
                                        transition: 'width 0.3s ease-in-out',
                                        borderRadius: 2
                                    }} />
                                </Box>
                            </Box>
                        )}
                    </Box>
                    <Chip 
                        label="Click to view" 
                        size="small" 
                        sx={{ 
                            backgroundColor: 'primary.main',
                            color: 'white',
                            fontSize: '0.7rem',
                            height: 24,
                            '& .MuiChip-label': {
                                px: 1
                            }
                        }} 
                    />
                </Box>
            </CardContent>
        </Card>
    );
};

